import dictionary from '@/i18n/hi.generated.json'
import templateDictionary from '@/i18n/hi.templates.generated.json'
import { currentLanguage } from '@/i18n/locale.store'

const hi: Record<string, string> = dictionary

interface CompiledTemplate {
  pattern: RegExp
  names: string[]
  hindi: string
  fixed: number
}

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * English templates with {name} placeholders (from the mobile ARB, LP-548) as anchored patterns, most fixed text
 * first so the more specific sentence wins. One with almost no fixed text ("{name}") would match anything: dropped.
 */
export function compileTemplates(templates: Record<string, string>): CompiledTemplate[] {
  const out: CompiledTemplate[] = []
  for (const [english, hindi] of Object.entries(templates)) {
    const names: string[] = []
    const parts = english.split(/\{(\w+)\}/)
    let source = ''
    parts.forEach((part, i) => {
      if (i % 2 === 0) source += escape(part)
      else {
        source += names.includes(part) ? `\\k<${part}>` : `(?<${part}>.+?)`
        if (!names.includes(part)) names.push(part)
      }
    })
    const fixed = parts.filter((_, i) => i % 2 === 0).join('').replace(/[^A-Za-z]/g, '').length
    if (fixed < 4 || names.length === 0) continue
    out.push({ pattern: new RegExp(`^${source}$`), names, hindi, fixed })
  }
  return out.sort((a, b) => b.fixed - a.fixed)
}

const templates = compileTemplates(templateDictionary as Record<string, string>)

/** The Hindi for a template function's English output: an exact sentence, else a matching template with its values filled in. */
export function translateFormatted(text: string, compiled: CompiledTemplate[] = templates): string {
  if (currentLanguage() !== 'hi') return text
  if (text in hi) return hi[text]
  for (const t of compiled) {
    const m = t.pattern.exec(text)
    if (m?.groups) {
      const values = m.groups
      return t.hindi.replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole)
    }
  }
  return text
}

/** The Hindi for an English sentence when the mobile ARB has one; otherwise the English itself. */
export function translate(text: string): string {
  if (currentLanguage() !== 'hi') return text
  return hi[text] ?? text
}

/** Translates strings at access time (template output when it matches a full sentence, nested objects recurse); the mobile ARB is the source of Hindi (`npm run i18n:build`). */
export function localized<T extends object>(strings: T): T {
  const cache = new WeakMap<object, unknown>()
  const wrap = (value: unknown): unknown => {
    if (typeof value === 'string') return translate(value)
    // Templates: translate a string result, matching the mobile ARB's placeholder templates too (LP-548); anything else
    // (JSX, numbers) passes through untouched. Fixed strings are never template-matched.
    if (typeof value === 'function') {
      return (...args: unknown[]) => {
        const result = (value as (...a: unknown[]) => unknown)(...args)
        return typeof result === 'string' ? translateFormatted(result) : result
      }
    }
    // Lists of sentences (help steps): a translated copy, so array methods keep working.
    if (Array.isArray(value)) return value.map(wrap)
    // Only plain nested objects are proxied — never class instances, React elements or Dates.
    if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      const hit = cache.get(value)
      if (hit) return hit
      const proxy = new Proxy(value as object, { get: (target, prop, receiver) => wrap(Reflect.get(target, prop, receiver)) })
      cache.set(value, proxy)
      return proxy
    }
    return value
  }
  return wrap(strings) as T
}

export const hasHindi = (text: string) => text in hi
export const hindiEntries = () => Object.keys(hi).length
export const hindiTemplates = () => templates.length
